// The phone's foreground sender for lots' desired states (BL-2681, ADR 070 — Ingest Protocol § 7):
// each lot with something to send becomes one `PUT /api/lots/:id/state`, lots independent of each
// other, one request in flight per lot. The build without the native engine, and Android until its
// engine ships, send through this (the same requests the native engine will).
import type { ChangeQueueJson } from '@auctogon/admin-isomorphic/isomorphic/offline/change-queue-entry';
import { MalformedChangeError, ReplayTransportError, type ReplayTransport } from './replay-transport';
import {
  buildStateRequest,
  isEmptyWrite,
  markFailed,
  markRefused,
  markRetryLater,
  markSyncing,
  sendIdentity,
  settleSent,
  type LotConflictField,
  type LotDesiredState,
} from '@auctogon/admin-isomorphic/isomorphic/offline/lot-desired-state';
import { mediaOf } from '@auctogon/admin-isomorphic/isomorphic/offline/lot-state-media';
import { classifyStateResponse } from '@auctogon/admin-isomorphic/isomorphic/offline/lot-state-outcome';
import { lotPatchBaseline } from '../../components/lots/lot-edit-payloads';
import type { OfflineMediaStaging, OfflineStore } from '../../gateways/native-device';
import { sendMediaAdds } from './lot-media-sender';

/** What the sender needs from the replay that owns it. */
export interface LotStateSenderDeps {
  store: OfflineStore;
  transport: ReplayTransport;
  /** The staged photos, videos and posters (BL-2815), or null on a build with none. */
  media?: OfflineMediaStaging | null;
  /** Tells the queue's readers something changed. */
  notify: () => void;
  /** Not aborted, closed or offline, and still the session's scope (BUG-2064). */
  maySend: () => boolean | Promise<boolean>;
  logError?: (message: string, err: Error) => void;
  /** Makes a lowercase v4 UUID for a send's op id. */
  mint?: () => string;
}

/** How one round of sends went. */
export interface LotStateRound {
  /** Requests sent. */
  attempted: number;
  /** Lots left refused or failed. */
  failed: number;
}

/**
 * Whether a lot's state is waiting on something else to go first: a relist copy's client id names
 * an entry still queued, so a state for it must not be sent as a create under that id.
 */
function isHeld(state: LotDesiredState, queuedIds: ReadonlySet<string>): boolean {
  return queuedIds.has(state.lotId);
}

/**
 * Send every lot's desired state once: the lots whose state is pending, none of them in conflict or
 * failed, one request each. A lot that lands with something left (fields its create door did not
 * store, or a Save made meanwhile) is left pending for the caller's next round.
 *
 * @param deps - the store, transport and checks.
 * @param input.signal - aborts the request in flight.
 * @param input.tried - lot ids a server error already left pending this pass: not sent again.
 * @returns how many requests were sent and how many lots ended failed or refused.
 * @throws {ReplayTransportError} when the server cannot be reached or the session expired; the
 *   lot is put back to pending first.
 * @sideEffect Writes the lots' states; sends lot-state writes.
 */
export async function sendLotStatesRound(
  deps: LotStateSenderDeps,
  input: { signal: AbortSignal; tried: Set<string> },
): Promise<LotStateRound> {
  const { store } = deps;
  const round: LotStateRound = { attempted: 0, failed: 0 };
  // A closed queue, or one whose scope is no longer the device's, changes nothing (BUG-2064), not even a `syncing` reset.
  if (!(await deps.maySend())) return round;
  // One listing a round. A kill mid-request leaves its lot `syncing`: it is sent again with the same op id.
  const listing = [...(await store.lotStates.list())];
  // Lots with no photos go first, then the rest smallest queue first: one lot's hundreds of photos never hold up another lot's edit.
  const queuedFiles = (lot: LotDesiredState) => mediaOf(lot).adds.length;
  const sendOrder = [...listing].sort((a, b) => queuedFiles(a) - queuedFiles(b));
  for (const state of listing) {
    if (state.status === 'syncing') await store.lotStates.update(state.lotId, (cur) => (cur?.status === 'syncing' ? { ...cur, status: 'pending' } : cur));
  }
  const queuedIds = new Set((await store.outbox.list()).map((entry) => entry.id));
  for (const listed of sendOrder) {
    if (input.tried.has(listed.lotId)) continue;
    if ((listed.status !== 'pending' && listed.status !== 'syncing') || isHeld(listed, queuedIds)) continue;
    if (isEmptyWrite(listed)) {
      // Dropped in one step that checks again: a Save made since the listing may have given it something to send.
      await store.lotStates.update(listed.lotId, (cur) => (cur != null && isEmptyWrite(cur) ? null : cur));
      deps.notify();
      continue;
    }
    const state = listed;
    if (!(await deps.maySend())) break;
    // BL-2815: a lot that exists on the server sends its queued photos and videos first, one request each.
    if (state.kind === 'edit' && mediaOf(state).adds.length > 0) {
      const media = await sendMediaAdds(deps, { lotId: state.lotId, signal: input.signal });
      round.attempted += media.sent;
      if (media.ended === 'failed') round.failed += 1;
      if (media.ended === 'retry') input.tried.add(state.lotId);
      if (media.ended !== 'ok') continue;
      const fresh = await store.lotStates.get(state.lotId);
      if (fresh == null || isEmptyWrite(fresh)) {
        await store.lotStates.update(state.lotId, (cur) => (cur != null && isEmptyWrite(cur) ? null : cur));
        deps.notify();
        continue;
      }
    }
    round.attempted += 1;
    const ended = await sendOne(deps, { lotId: state.lotId, signal: input.signal });
    if (ended === 'failed') round.failed += 1;
    if (ended === 'retry') input.tried.add(state.lotId);
  }
  return round;
}

async function sendOne(
  deps: LotStateSenderDeps,
  input: { lotId: string; signal: AbortSignal },
): Promise<'synced' | 'failed' | 'retry' | 'gone'> {
  const { store, transport } = deps;
  const mint = deps.mint ?? (() => globalThis.crypto.randomUUID());
  // The op id is stored, strictly, before the request leaves, so a kill mid-request resends the same one.
  let clientOpId = '';
  const sending = await store.lotStates.update(input.lotId, (cur) => {
    // Emptied by a Save since the round's listing (reverted to its base): nothing to send, the next round drops it.
    if (cur == null || cur.status !== 'pending' || isEmptyWrite(cur)) return cur;
    const identified = sendIdentity(markSyncing(cur), mint);
    clientOpId = identified.clientOpId;
    return identified.state;
  });
  if (sending == null || sending.status !== 'syncing') return 'gone';
  deps.notify();
  const request = buildStateRequest(sending, clientOpId);
  let answer: { status: number; body: unknown };
  try {
    answer = await transport.sendLotState({ lotId: input.lotId, body: request, signal: input.signal });
  } catch (err) {
    if (err instanceof MalformedChangeError) {
      await store.lotStates.update(input.lotId, (cur) => cur && markFailed(cur, { message: err.message, code: null, httpStatus: null, fieldNames: [] }));
      deps.notify();
      return 'failed';
    }
    await backToPending(store, input.lotId);
    deps.notify();
    throw new ReplayTransportError('change queue: the connection failed', { cause: err });
  }
  const outcome = classifyStateResponse(answer);
  switch (outcome.kind) {
    case 'synced':
      // What is left (fields the create door did not store, a Save made meanwhile) stays pending.
      await store.lotStates.update(input.lotId, (cur) =>
        cur ? settleSent(cur, request.fields, { removedMediaIds: request.removedMediaIds ?? [], positions: request.positions ?? null }) : null,
      );
      // The device's temporary "New lot" goes; the next sync brings the server's copy.
      if (sending.kind === 'create') await store.records.delete({ collection: 'lots', key: input.lotId });
      deps.notify();
      return 'synced';
    case 'refused': {
      const conflict = await readConflict(deps, { state: sending, fieldNames: outcome.error.fieldNames, signal: input.signal });
      await store.lotStates.update(input.lotId, (cur) => cur && markRefused(cur, outcome.error, conflict));
      deps.notify();
      return 'failed';
    }
    case 'failed':
      await store.lotStates.update(input.lotId, (cur) => cur && markFailed(cur, outcome.error));
      deps.notify();
      return 'failed';
    case 'retry-later': {
      const after = await store.lotStates.update(input.lotId, (cur) => cur && markRetryLater(cur, outcome.error));
      deps.notify();
      return after?.status === 'failed' ? 'failed' : 'retry';
    }
    case 'transport':
      await backToPending(store, input.lotId);
      deps.notify();
      throw new ReplayTransportError(`change queue: ${outcome.reason}`);
  }
}

async function backToPending(store: OfflineStore, lotId: string): Promise<void> {
  await store.lotStates.update(lotId, (cur) => (cur?.status === 'syncing' ? { ...cur, status: 'pending' } : cur));
}

/**
 * Both sides of a conflict: what the phone wants, and what the server holds now (read once, so
 * Conflict can show both). A server value that cannot be read is null, and the row still shows the
 * phone's.
 */
async function readConflict(
  deps: LotStateSenderDeps,
  input: { state: LotDesiredState; fieldNames: string[]; signal: AbortSignal },
): Promise<LotConflictField[]> {
  let theirs: Record<string, ChangeQueueJson> | null = null;
  try {
    const answer = await deps.transport.readLot({ lotId: input.state.lotId, signal: input.signal });
    const body = answer.body as { success?: boolean; data?: unknown } | null;
    if (answer.status === 200 && body?.success === true && body.data != null) {
      theirs = lotPatchBaseline(body.data as Parameters<typeof lotPatchBaseline>[0]) as unknown as Record<string, ChangeQueueJson>;
    } else {
      deps.logError?.('change queue: the server value for a conflict could not be read', new Error(`the lot read answered ${answer.status}`));
    }
  } catch (err) {
    deps.logError?.('change queue: the server value for a conflict could not be read', err instanceof Error ? err : new Error(String(err)));
  }
  return input.fieldNames.map((field) => {
    const known = theirs != null && field in theirs;
    return {
      field,
      mine: input.state.fields[field] ?? null,
      theirs: known ? (theirs?.[field] ?? null) : null,
      ...(known ? {} : { theirsUnread: true }),
    };
  });
}
